// Impact Analysis types and Zod schemas (ARCHITECTURE.md §4.6).
//
// An ImpactReport answers "given these changed files, what else is affected?".
// It records directly and transitively affected entries (each carrying the
// Evidence that justified the link), the artifacts those entries belong to, the
// validations / agents / workflows that are implicated, and a test mapping for
// regression scoping.
//
// As elsewhere in the codebase, the inferred Zod types are the compile-time
// source of truth; the schemas provide runtime validation at trust boundaries
// (cache deserialization, plugin input).

import { z } from "zod";

import { EvidenceSchema } from "../evidence/types.js";

// ---------------------------------------------------------------------------
// ArtifactRoute
// ---------------------------------------------------------------------------

// Captures the artifact-to-paths association the impact layer needs.
export const ArtifactRouteSchema = z.object({
  artifactId: z.string(),
  paths: z.array(z.string()),
});

export type ArtifactRoute = z.infer<typeof ArtifactRouteSchema>;

// ---------------------------------------------------------------------------
// AffectedEntry
// ---------------------------------------------------------------------------

// A single file (or symbol) reached during impact traversal. `depth` is the BFS
// distance from the changed set (0 for the changed files themselves);
// `evidence` records why the entry was reached.
//
// When the reached node is a symbol (§5.2), `symbolId` / `symbolName` identify
// it and `line` / `endLine` give its 1-based source range; `path` still carries
// the symbol's containing file so file-level consumers keep working unchanged.
export const AffectedEntrySchema = z.object({
  path: z.string(),
  artifactId: z.string().optional(),
  /** Symbol id (filePath#name) when the reached node is a symbol. */
  symbolId: z.string().optional(),
  /** Symbol display name when the reached node is a symbol. */
  symbolName: z.string().optional(),
  /** 1-based start line of the symbol's scope. */
  line: z.number().int().nonnegative().optional(),
  /** 1-based end line of the symbol's scope. */
  endLine: z.number().int().nonnegative().optional(),
  depth: z.number().int().nonnegative(),
  evidence: z.array(EvidenceSchema),
  /**
   * Deterministic relevance score derived from the path's accumulated evidence
   * (Issue #21). Used to rank affected entries; optional for backward compat.
   */
  relevanceScore: z.number().optional(),
});

export type AffectedEntry = z.infer<typeof AffectedEntrySchema>;

// ---------------------------------------------------------------------------
// AffectedArtifact
// ---------------------------------------------------------------------------

// An artifact implicated by the change, with the files that triggered it and
// the validations / owner agents / routes derived from the contract overlay.
export const AffectedArtifactSchema = z.object({
  artifactId: z.string(),
  affectedFiles: z.array(z.string()),
  requiredValidations: z.array(z.string()),
  ownerAgents: z.array(z.string()),
  routes: z.array(ArtifactRouteSchema),
});

export type AffectedArtifact = z.infer<typeof AffectedArtifactSchema>;

// ---------------------------------------------------------------------------
// TestAssociation
// ---------------------------------------------------------------------------

export const TEST_ASSOCIATION_METHODS = [
  "import",
  "naming_convention",
  "directory_convention",
  "semantic",
] as const;

export const TestAssociationMethodSchema = z.enum(TEST_ASSOCIATION_METHODS);

export type TestAssociationMethod = z.infer<typeof TestAssociationMethodSchema>;

// Maps a source file to the tests that cover it, recording how the association
// was discovered.
export const TestAssociationSchema = z.object({
  sourcePath: z.string(),
  testPaths: z.array(z.string()),
  method: TestAssociationMethodSchema,
});

export type TestAssociation = z.infer<typeof TestAssociationSchema>;

// ---------------------------------------------------------------------------
// ImpactReport
// ---------------------------------------------------------------------------

export const IMPACT_DIRECTIONS = ["downstream", "upstream"] as const;

export const ImpactDirectionSchema = z.enum(IMPACT_DIRECTIONS);

export type ImpactDirection = z.infer<typeof ImpactDirectionSchema>;

export const ImpactReportSchema = z.object({
  changed: z.array(z.string()),
  direction: ImpactDirectionSchema,
  directlyAffected: z.array(AffectedEntrySchema),
  transitivelyAffected: z.array(AffectedEntrySchema),
  affectedArtifacts: z.array(AffectedArtifactSchema),
  affectedValidations: z.array(z.string()),
  affectedAgents: z.array(z.string()),
  affectedWorkflows: z.array(z.string()),
  testMapping: z.array(TestAssociationSchema),
  /** Maximum edge confidence across the report, in the range 0.0 – 1.0. */
  maxConfidence: z.number().min(0).max(1),
  /** ISO-8601 timestamp of when the report was generated. */
  generatedAt: z.string(),
});

export type ImpactReport = z.infer<typeof ImpactReportSchema>;
